import React, { useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, PieChart, Pie, Cell
} from 'recharts';

const CHART_COLORS = ['#6366f1', '#22d3ee', '#f59e0b', '#ef4444', '#10b981', '#a855f7', '#f97316'];

export default function RevenueChart({ orders }) {
  // Aggregate orders by date
  const dailyData = useMemo(() => {
    const dayMap = {};
    orders.forEach((order) => {
      if (!order.timestamp) return;
      const date = new Date(order.timestamp);
      const key = date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
      if (!dayMap[key]) dayMap[key] = { date: key, revenue: 0, cups: 0, ts: date.getTime() };
      dayMap[key].revenue += Number(order.amount) || 19;
      dayMap[key].cups += 1;
    });

    return Object.values(dayMap).sort((a, b) => a.ts - b.ts).slice(-14); // Last 14 days
  }, [orders]);

  // Beverage popularity
  const beverageData = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      if (!order.item_id) return;
      const name = order.item_id.replace('juice_', '').replace(/_/g, ' ').toUpperCase();
      if (!map[name]) map[name] = { name, count: 0 };
      map[name].count += 1;
    });
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [orders]);

  // Kiosk performance
  const kioskData = useMemo(() => {
    const map = {};
    orders.forEach((order) => {
      if (!order.kiosk_id) return;
      const name = order.kiosk_id.replace('CHILL-RAS-', '');
      if (!map[name]) map[name] = { name, revenue: 0, cups: 0 };
      map[name].revenue += Number(order.amount) || 19;
      map[name].cups += 1;
    });
    return Object.values(map).sort((a, b) => b.revenue - a.revenue);
  }, [orders]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-label">{label}</p>
          {payload.map((entry, i) => (
            <p key={i} className="chart-tooltip-value" style={{ color: entry.color }}>
              {entry.name}: {entry.name === 'revenue' ? `₹${entry.value}` : entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (orders.length === 0) {
    return (
      <div className="section">
        <h2 className="section-title">
          <span className="section-title-icon">📈</span>
          Analytics
        </h2>
        <div className="empty-state">
          <span className="empty-state-icon">📊</span>
          <p>No data available for analytics yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="section">
      <h2 className="section-title">
        <span className="section-title-icon">📈</span>
        Analytics
      </h2>

      <div className="charts-grid">
        {/* Revenue Trend */}
        <div className="chart-card chart-card--wide">
          <h3 className="chart-card-title">Revenue Trend (Last 14 Days)</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={dailyData}>
                <defs>
                  <linearGradient id="gradientRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="revenue"
                  stroke="#6366f1"
                  strokeWidth={2.5}
                  fill="url(#gradientRevenue)"
                  dot={{ fill: '#6366f1', r: 4, strokeWidth: 0 }}
                  activeDot={{ r: 6, stroke: '#6366f1', strokeWidth: 2, fill: '#1e1b4b' }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Cups Per Day */}
        <div className="chart-card chart-card--wide">
          <h3 className="chart-card-title">Daily Cups Dispensed</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={dailyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="cups" fill="#22d3ee" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Beverage Popularity */}
        <div className="chart-card">
          <h3 className="chart-card-title">Beverage Popularity</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={beverageData}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={100}
                  paddingAngle={4}
                  dataKey="count"
                  nameKey="name"
                >
                  {beverageData.map((_, i) => (
                    <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ backgroundColor: '#1e293b', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#f1f5f9' }}
                />
              </PieChart>
            </ResponsiveContainer>
            <div className="chart-legend">
              {beverageData.map((item, i) => (
                <div key={item.name} className="chart-legend-item">
                  <span className="chart-legend-dot" style={{ backgroundColor: CHART_COLORS[i % CHART_COLORS.length] }} />
                  <span className="chart-legend-label">{item.name}</span>
                  <span className="chart-legend-value">{item.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Kiosk Performance */}
        <div className="chart-card">
          <h3 className="chart-card-title">Kiosk Performance</h3>
          <div className="chart-wrapper">
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={kioskData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} />
                <YAxis dataKey="name" type="category" tick={{ fill: '#94a3b8', fontSize: 12 }} axisLine={false} tickLine={false} width={80} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="revenue" fill="#10b981" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
}
